import 'react-photo-view/dist/react-photo-view.css';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import { MediaViewerToolbar } from './MediaViewerToolbar';
import { Stack, Typography } from '@mui/material';
import { abyssColors } from '@/theme/colors';
import { isImageFile } from '@/utils';
import { Attachment } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/types';
import React, { ReactElement, useMemo } from 'react';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';

type MediaTitleProps = {
  title?: string;
};

const MediaTitle = ({ title }: MediaTitleProps) => {
  const navbarHeight = useNavbarHeight();
  return (
    <Stack height={`${navbarHeight}px`} alignItems="center" justifyContent="center">
      <Typography color={abyssColors.neutral[200]}>{title}</Typography>
    </Stack>
  );
};

type MediaViewerProps = {
  disableDelete?: boolean;
  children: ReactElement[] | ReactElement;
  mediaFiles: Attachment[];
  handleRemoveAttachment?: (argument0: number) => void;
  handleDownloadAttachment?: (argument0: number) => void;
};

export const MediaViewer = ({
  disableDelete,
  children,
  mediaFiles,
  handleDownloadAttachment,
  handleRemoveAttachment,
}: MediaViewerProps) => {
  const memoizedChildren = useMemo(() => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        return null;
      }

      const mediaFile = mediaFiles.find((file) => file && file.id === child.key);

      if (!mediaFile) {
        return <React.Fragment key={child.key}>{child}</React.Fragment>;
      }

      if (!isImageFile(mediaFile)) {
        return <React.Fragment key={child.key}>{child}</React.Fragment>;
      }

      return (
        <React.Fragment key={child.key}>
          <PhotoView src={mediaFile.base64}>{child}</PhotoView>
        </React.Fragment>
      );
    });
  }, [children, mediaFiles]);

  return (
    <PhotoProvider
      maskOpacity={0.8}
      toolbarRender={({ onScale, scale, index, onClose }) => {
        if (!mediaFiles[index] || !isImageFile(mediaFiles[index])) {
          return (
            <MediaViewerToolbar
              onClose={onClose}
              onDeleteClick={() => {
                if (handleRemoveAttachment) handleRemoveAttachment(index);
              }}
            />
          );
        }
        return (
          <MediaViewerToolbar
            disableDelete={disableDelete}
            onClose={onClose}
            onScale={onScale}
            scale={scale}
            onDownloadClick={() => {
              if (handleDownloadAttachment) handleDownloadAttachment(index);
            }}
            onDeleteClick={() => {
              if (handleRemoveAttachment) handleRemoveAttachment(index);
            }}
          />
        );
      }}
      overlayRender={({ index }) => {
        return (
          <MediaTitle
            title={index < mediaFiles.length && mediaFiles[index] ? mediaFiles[index].name : ''}
          />
        );
      }}
    >
      {memoizedChildren}
    </PhotoProvider>
  );
};
